import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface RailLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  /** Plain-anchor destination. Mutually exclusive with `to`. */
  href?: string;
  /** React-router destination — when set, renders a `<Link>` for SPA nav. */
  to?: string;
  /** Leading icon slot (rendered inside a `.pn-rail-link-ico` wrapper). */
  icon?: ReactNode;
  /** Trailing count badge (rendered inside a `.pn-rail-link-badge` pill). */
  badge?: ReactNode;
  /** When true, sets `aria-current="page"` and applies the active styling. */
  active?: boolean;
  children: ReactNode;
}

/**
 * Navigation rail link — left rail row with optional icon + label + optional
 * trailing count badge. Active state draws an accent left-bar. Matches
 * dashboard.html `.rail-link`.
 *
 * Pass `to` for in-app routes (renders react-router `<NavLink>`) or `href`
 * for plain anchors. The shell composition (`<PenroseLeftRail>`) drives
 * `active` from `useLocation()`.
 */
export function RailLink({
  href,
  to,
  icon,
  badge,
  active,
  children,
  className,
  ...rest
}: RailLinkProps) {
  const cls = ['pn-rail-link', className].filter(Boolean).join(' ');
  const inner = (
    <>
      {icon != null ? (
        <span className="pn-rail-link-ico" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="pn-rail-link-label">{children}</span>
      {badge != null ? <span className="pn-rail-link-badge">{badge}</span> : null}
    </>
  );

  if (to != null) {
    return (
      <Link
        to={to}
        className={cls}
        aria-current={active ? 'page' : undefined}
        {...rest}
      >
        {inner}
      </Link>
    );
  }
  return (
    <a
      href={href ?? '#'}
      className={cls}
      aria-current={active ? 'page' : undefined}
      {...rest}
    >
      {inner}
    </a>
  );
}
